import { Dispatch, FC, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { IonHeader, IonPage } from '@ionic/react'
import cx from 'classnames'

import { CloseCircle } from '@/components/atoms/assets/CloseCircle'
import { InterestButton } from '@/components/atoms/InterestButton'
import { Loading } from '@/components/atoms/Loading'
import { Title } from '@/components/atoms/Title'

import { Category } from '@/services/categories'
import { useCategories } from '@/lib/hooks/categories'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { Tracking, TrackingEvent } from '@/lib/tracking'
import { isIosNativeOrPWA } from '@/utils/utils'

type CategoriesModalProps = {
  setSelectedCategory: Dispatch<SetStateAction<Category>>
  onDismiss: () => void
}

export const CategoriesModal: FC<CategoriesModalProps> = ({
  setSelectedCategory,
  onDismiss,
}) => {
  const { data: userInfo } = useUserInfo()
  const { t } = useTranslation('common')
  const { data: categories, isLoading: isLoadingCategories } = useCategories()

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category)
    onDismiss()

    if (!(userInfo?.id && category?.id && category?.name)) return

    Tracking.triggerEvent(TrackingEvent.ExploreCategorySelected, {
      categoryName: category.name,
      categoryID: category.id,
      userID: userInfo.id,
    })
  }

  return (
    <IonPage>
      <div
        className={cx('px-8 flex flex-col', {
          'pt-16': isIosNativeOrPWA(),
          'pt-8': !isIosNativeOrPWA(),
        })}
      >
        <IonHeader className="flex">
          <Title level={2} className="text-[7vw] sm:text-4xl mb-7">
            {t('discover.categoriesTitle')}
          </Title>
          <CloseCircle
            className="w-8 h-8 sm:w-12 sm:h-12 ml-auto -mr-4"
            onClick={onDismiss}
          />
        </IonHeader>
        {isLoadingCategories ? (
          <Loading />
        ) : (
          <div
            className={cx('py-4 grow overflow-y-scroll', {
              'basis-[calc(100vh-10rem)] sm:basis-[calc(100vh-10.75rem)]':
                isIosNativeOrPWA(),
              'basis-[calc(100vh-7.75rem)] sm:basis-[calc(100vh-8.5rem)]':
                !isIosNativeOrPWA(),
            })}
          >
            {categories?.map((category) => (
              <InterestButton
                key={category.id}
                className="mx-2 my-1 ml-0"
                label={category.name}
                value={category.id}
                onClick={() => handleCategoryClick(category)}
              />
            ))}
          </div>
        )}
      </div>
    </IonPage>
  )
}
